import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { GraduationCap, Plus, Pencil, Trash2, ChevronsUpDown, Check, Calendar, BookOpen, MapPin, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { MonthYearPicker } from './MonthYearPicker';
import { detectTextDirection } from '@/lib/textDirection';
import { COUNTRY_CITIES } from '@/data/locationData';
import { COUNTRY_AR, CITY_AR } from '@/data/arabicLookups';
import { sortEducationNewestFirst } from '@/lib/timelineSorting';

const DEGREE_OPTIONS = [
  'High School Diploma', 'GED', 'Vocational Certificate', 'Diploma',
  "Associate's Degree (AA/AS)", "Bachelor's Degree (BA/BS)", "Bachelor of Engineering (BEng)",
  "Bachelor of Science (BSc)", "Bachelor of Arts (BA)", "Bachelor of Business Administration (BBA)",
  "Bachelor of Laws (LLB)", "Master's Degree (MA/MS)", "Master of Business Administration (MBA)",
  "Master of Engineering (MEng)", "Master of Science (MSc)", "Master of Arts (MA)",
  "Master of Laws (LLM)", "Master of Public Administration (MPA)", "Doctor of Philosophy (PhD)",
  "Doctor of Medicine (MD)", "Doctor of Education (EdD)", "Doctor of Business Administration (DBA)",
  "Juris Doctor (JD)", "Professional Certification", "Postgraduate Diploma (PGDip)", "Postdoctoral Fellowship",
];

const GRADE_TYPE_OPTIONS_EN = [
  { value: 'gpa_4', label: 'GPA (out of 4.0)', scale: '4' },
  { value: 'gpa_5', label: 'GPA (out of 5.0)', scale: '5' },
  { value: 'percentage', label: 'Percentage (out of 100)', scale: '100' },
  { value: 'letter', label: 'Letter Grade (A, B, C…)', scale: '' },
  { value: 'classification', label: 'Classification (First, 2:1…)', scale: '' },
  { value: 'other', label: 'Other', scale: '' },
];

const GRADE_TYPE_OPTIONS_AR = [
  { value: 'gpa_4', label: 'المعدل التراكمي (من 4.0)', scale: '4' },
  { value: 'gpa_5', label: 'المعدل التراكمي (من 5.0)', scale: '5' },
  { value: 'percentage', label: 'النسبة المئوية (من 100)', scale: '100' },
  { value: 'letter', label: 'تقدير حرفي (A, B, C…)', scale: '' },
  { value: 'classification', label: 'تصنيف (امتياز، جيد جداً…)', scale: '' },
  { value: 'other', label: 'أخرى', scale: '' },
];

const DEGREE_OPTIONS_AR: Record<string, string> = {
  'High School Diploma': 'شهادة الثانوية العامة',
  'GED': 'شهادة معادلة الثانوية',
  'Vocational Certificate': 'شهادة مهنية',
  'Diploma': 'دبلوم',
  "Associate's Degree (AA/AS)": 'درجة مشارك',
  "Bachelor's Degree (BA/BS)": 'بكالوريوس',
  "Bachelor of Engineering (BEng)": 'بكالوريوس هندسة',
  "Bachelor of Science (BSc)": 'بكالوريوس علوم',
  "Bachelor of Arts (BA)": 'بكالوريوس آداب',
  "Bachelor of Business Administration (BBA)": 'بكالوريوس إدارة أعمال',
  "Bachelor of Laws (LLB)": 'بكالوريوس قانون',
  "Master's Degree (MA/MS)": 'ماجستير',
  "Master of Business Administration (MBA)": 'ماجستير إدارة أعمال',
  "Master of Engineering (MEng)": 'ماجستير هندسة',
  "Master of Science (MSc)": 'ماجستير علوم',
  "Master of Arts (MA)": 'ماجستير آداب',
  "Master of Laws (LLM)": 'ماجستير قانون',
  "Master of Public Administration (MPA)": 'ماجستير إدارة عامة',
  "Doctor of Philosophy (PhD)": 'دكتوراه فلسفة',
  "Doctor of Medicine (MD)": 'دكتوراه طب',
  "Doctor of Education (EdD)": 'دكتوراه تربية',
  "Doctor of Business Administration (DBA)": 'دكتوراه إدارة أعمال',
  "Juris Doctor (JD)": 'دكتوراه قانون',
  "Professional Certification": 'شهادة مهنية احترافية',
  "Postgraduate Diploma (PGDip)": 'دبلوم دراسات عليا',
  "Postdoctoral Fellowship": 'زمالة ما بعد الدكتوراه',
};

interface Education {
  id: string;
  institution_name: string;
  degree: string | null;
  field_of_study: string | null;
  start_date: string | null;
  end_date: string | null;
  grade: string | null;
  grade_type: string | null;
  description: string | null;
  location: string | null;
}

interface Props {
  education: Education[];
  profileId: string;
  onAdd: (data: Record<string, unknown>) => void;
  onUpdate: (id: string, data: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  saving: boolean;
  editLocked?: boolean;
}

const empty = { institution_name: '', degree: '', field_of_study: '', start_date: '', end_date: '', grade: '', grade_type: '', description: '', location: '' };

function DegreeCombobox({ value, onChange, isAr }: { value: string; onChange: (v: string) => void; isAr?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const options = useMemo(() => DEGREE_OPTIONS.map(d => ({ value: d, label: isAr ? (DEGREE_OPTIONS_AR[d] ?? d) : d })), [isAr]);
  const filtered = options.filter(d => d.label.toLowerCase().includes(search.toLowerCase()) || d.value.toLowerCase().includes(search.toLowerCase()));
  const selectedLabel = options.find(o => o.value === value)?.label ?? value;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal h-10 text-sm">
          <span className={cn('truncate', !value && 'text-muted-foreground')}>{selectedLabel || t('seek.profile.selectDegree')}</span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="p-2 border-b border-border">
          <Input placeholder={t('seek.profile.searchOrType')} value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-sm" autoFocus />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filtered.map((d) => (
            <button key={d.value} type="button" className={cn('flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors text-start', value === d.value && 'bg-seek/10 text-seek')} onClick={() => { onChange(d.value); setOpen(false); setSearch(''); }}>
              <Check className={cn('h-3.5 w-3.5 shrink-0', value === d.value ? 'opacity-100' : 'opacity-0')} />
              {d.label}
            </button>
          ))}
          {search && !filtered.some(d => d.label.toLowerCase() === search.toLowerCase()) && (
            <button type="button" className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors text-start text-seek font-medium" onClick={() => { onChange(search); setOpen(false); setSearch(''); }}>
              <Plus className="h-3.5 w-3.5 shrink-0" /> {t('seek.profile.useCustom', { val: search })}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function LocationCombobox({ value, onChange, isAr }: { value: string; onChange: (v: string) => void; isAr?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const translateLoc = (loc: string) => {
    if (!isAr) return loc;
    if (loc.includes(', ')) {
      const [city, country] = loc.split(', ');
      return `${CITY_AR[city] ?? city}، ${COUNTRY_AR[country] ?? country}`;
    }
    return COUNTRY_AR[loc] ?? loc;
  };

  const locations = useMemo(() => {
    const list: { value: string; label: string }[] = [];
    Object.entries(COUNTRY_CITIES).forEach(([country, cities]) => {
      list.push({ value: country, label: isAr ? (COUNTRY_AR[country] ?? country) : country });
      cities.forEach(city => {
        const val = `${city}, ${country}`;
        const label = isAr ? `${CITY_AR[city] ?? city}، ${COUNTRY_AR[country] ?? country}` : val;
        list.push({ value: val, label });
      });
    });
    return list;
  }, [isAr]);

  const filtered = useMemo(() => {
    if (!search.trim()) return locations.filter(l => !l.value.includes(','));
    const q = search.toLowerCase();
    return locations.filter(l => l.label.toLowerCase().includes(q) || l.value.toLowerCase().includes(q)).slice(0, 50);
  }, [search, locations]);

  const selectedLabel = locations.find(l => l.value === value)?.label ?? (value ? translateLoc(value) : '');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal h-10 text-sm">
          <span className={cn('truncate', !value && 'text-muted-foreground')}>{selectedLabel || t('seek.profile.selectLocation')}</span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="me-2 h-4 w-4 shrink-0 opacity-50" />
          <input className="flex h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" placeholder={t('seek.profile.searchCityCountry')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="max-h-[200px] overflow-y-auto p-1">
          {filtered.map((loc) => (
            <button key={loc.value} type="button" className={cn('flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors text-start', value === loc.value && 'bg-seek/10 text-seek')} onClick={() => { onChange(loc.value); setOpen(false); setSearch(''); }}>
              <Check className={cn('h-3.5 w-3.5 shrink-0', value === loc.value ? 'opacity-100' : 'opacity-0')} />
              {loc.label}
            </button>
          ))}
          {search && !filtered.some(l => l.label.toLowerCase() === search.toLowerCase()) && (
            <button type="button" className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors text-start text-seek font-medium" onClick={() => { onChange(search); setOpen(false); setSearch(''); }}>
              <Plus className="h-3.5 w-3.5 shrink-0" /> {t('seek.profile.useCustom', { val: search })}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function formatYear(dateStr: string | null): string {
  if (!dateStr) return '';
  try { return new Date(dateStr).getFullYear().toString(); } catch { return dateStr; }
}

export function EducationSection({ education, profileId, onAdd, onUpdate, onDelete, saving, editLocked }: Props) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const GRADE_TYPE_OPTIONS = isAr ? GRADE_TYPE_OPTIONS_AR : GRADE_TYPE_OPTIONS_EN;
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(empty);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const sortedEducation = useMemo(
    () => sortEducationNewestFirst(education),
    [education],
  );

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sectionId === 'section-education') {
        if (detail.editId) {
          const target = education.find((edu) => edu.id === detail.editId);
          if (target) {
            openEdit(target);
            return;
          }
        }
        openNew();
      }
    };
    window.addEventListener('open-section-edit', handler);
    return () => window.removeEventListener('open-section-edit', handler);
  }, [education]);

  const openEdit = (edu: Education) => {
    setEditId(edu.id);
    setForm({
      institution_name: edu.institution_name, degree: edu.degree ?? '', field_of_study: edu.field_of_study ?? '',
      start_date: edu.start_date ?? '', end_date: edu.end_date ?? '', grade: edu.grade ?? '', grade_type: edu.grade_type ?? '',
      description: edu.description ?? '', location: edu.location ?? '',
    });
    setOpen(true);
  };

  const eduDateError = form.start_date && form.end_date && form.start_date > form.end_date ? 'End date cannot be before start date' : null;
  const gradeTypeError = form.grade && !form.grade_type ? t('seek.profile.gradeTypeRequired') : null;

  const handleSave = () => {
    if (eduDateError || gradeTypeError) return;
    const toDate = (v: string) => v && !v.match(/^\d{4}-\d{2}-\d{2}$/) ? `${v}-01` : v;
    const payload = { ...form, start_date: toDate(form.start_date) || null, end_date: toDate(form.end_date) || null, candidate_profile_id: profileId };
    if (editId) onUpdate(editId, payload);
    else onAdd(payload);
    setOpen(false);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground font-[var(--font-display)]">
            <GraduationCap className="w-5 h-5 text-seek" />
            {t('seek.profile.education')}
          </h3>
          <Button variant="ghost" size="sm" onClick={openNew} disabled={editLocked} className="gap-1 bg-seek/10 text-seek hover:bg-seek hover:text-white">
            <Plus className="w-4 h-4" /> {t('seek.profile.add')}
          </Button>
        </div>

        {education.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-seek/10 flex items-center justify-center mb-3">
              <BookOpen className="w-5 h-5 text-seek" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">{t('seek.profile.noEducation')}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {sortedEducation.map((edu) => {
              const startYear = formatYear(edu.start_date);
              const endYear = formatYear(edu.end_date);
              const dateRange = startYear && endYear ? `${startYear} – ${endYear}` : startYear || endYear || '';
              return (
                <div
                  key={edu.id}
                  className="group relative flex items-start gap-4 rounded-xl border bg-card p-4 hover:border-seek/30 hover:shadow-sm transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-seek/10 flex items-center justify-center shrink-0 mt-0.5">
                    <GraduationCap className="w-4.5 h-4.5 text-seek" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground leading-snug" dir={detectTextDirection(edu.degree || edu.institution_name)}>
                          {edu.degree ? `${edu.degree}${edu.field_of_study ? ` in ${edu.field_of_study}` : ''}` : edu.institution_name}
                        </p>
                        <p className="text-xs font-medium text-seek mt-0.5" dir={detectTextDirection(edu.institution_name)}>{edu.institution_name}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(edu)} disabled={editLocked}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget({ id: edu.id, name: edu.degree || edu.institution_name })} disabled={editLocked}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {dateRange && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" /> {dateRange}
                        </span>
                      )}
                      {edu.location && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="w-3 h-3" /> {edu.location}
                        </span>
                      )}
                      {edu.grade && (
                        <span className="text-xs text-muted-foreground">
                          {edu.grade}{(() => { const opt = GRADE_TYPE_OPTIONS.find(o => o.value === edu.grade_type); return opt?.scale ? ` ${t('seek.profile.outOf')} ${opt.scale}` : ''; })()}
                        </span>
                      )}
                    </div>
                    {edu.description && (
                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed line-clamp-2" dir={detectTextDirection(edu.description)}>{edu.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? t('seek.profile.editEducation') : t('seek.profile.addEducation')}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>{t('seek.profile.institution')} *</Label><Input value={form.institution_name} onChange={(e) => setForm(p => ({ ...p, institution_name: e.target.value }))} /></div>
            <div><Label>{t('seek.profile.degree')}</Label><DegreeCombobox value={form.degree} onChange={(v) => setForm(p => ({ ...p, degree: v }))} isAr={isAr} /></div>
            <div><Label>{t('seek.profile.fieldOfStudy')}</Label><Input value={form.field_of_study} onChange={(e) => setForm(p => ({ ...p, field_of_study: e.target.value }))} /></div>
            <div><Label>{t('seek.profile.startDate')}</Label><MonthYearPicker value={form.start_date?.substring(0, 7)} onChange={(v) => setForm(p => ({ ...p, start_date: v }))} /></div>
            <div><Label>{t('seek.profile.endDate')}</Label><MonthYearPicker value={form.end_date?.substring(0, 7)} onChange={(v) => setForm(p => ({ ...p, end_date: v }))} /></div>
            <div><Label>{t('seek.profile.location')}</Label><LocationCombobox value={form.location} onChange={(v) => setForm(p => ({ ...p, location: v }))} isAr={isAr} /></div>
            <div>
              <Label>{t('seek.profile.gradeType')}</Label>
              <Select value={form.grade_type} onValueChange={(v) => setForm(p => ({ ...p, grade_type: v }))}>
                <SelectTrigger className={gradeTypeError ? 'border-destructive' : ''}><SelectValue placeholder={t('seek.profile.selectGradeType')} /></SelectTrigger>
                <SelectContent>
                  {GRADE_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {gradeTypeError && <p className="text-xs text-destructive mt-1">{gradeTypeError}</p>}
            </div>
            <div><Label>{t('seek.profile.gradeGpa')}</Label><Input value={form.grade} onChange={(e) => setForm(p => ({ ...p, grade: e.target.value }))} placeholder={form.grade_type === 'gpa_4' ? 'e.g. 3.8' : form.grade_type === 'gpa_5' ? 'e.g. 4.5' : form.grade_type === 'percentage' ? 'e.g. 85' : form.grade_type === 'letter' ? 'e.g. A' : ''} /></div>
            <div className="sm:col-span-2"><Label>{t('seek.profile.description')}</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            {eduDateError && <p className="text-xs text-destructive">{eduDateError}</p>}
            <Button onClick={handleSave} disabled={!form.institution_name || !!eduDateError || saving} className="bg-seek text-white hover:bg-seek/90">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('seek.profile.deleteEducation')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('seek.profile.deleteEducationConfirm', { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { onDelete(deleteTarget.id); setDeleteTarget(null); } }}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
